<template>
  <div class="contact-search">
    <div class="row">
      <ContactSearchForm v-model="searchTerm" @submit="performSearch" />
    </div>

    <div class="row">
      <div class="col-12">
        <h2 class="h4 text-dark">Search within</h2>

        <ContactSearchCategoriesFilter
          v-model="selectedCategories"
          :categories="categories"
          @change="performSearch"
        />
      </div>
    </div>

    <div class="contact-search__results">
      <transition name="fade">
        <div
          v-if="loading"
          class="contact-search__loading-overlay"
          role="status"
        >
          <i class="far fa-circle-notch fa-spin"></i>
          <span>Loading</span>
        </div>
        <div
          v-else-if="hasSearchTerms && !hasSearchResults && !loading"
          class="alert alert-info"
          role="alert"
        >
          We could not find any results. Please try to change your filters
          and/or search terms.
        </div>
        <div
          v-else-if="!hasSearchTerms && !hasSearchResults && dirty"
          class="alert alert-secondary py-4"
          role="alert"
        >
          <h4>Please enter a search term or select a filter.</h4>
          <p>
            You can search for contact persons and locations by name, title,
            phone number etc.
          </p>
        </div>
      </transition>

      <div v-if="sortedLocationResults.length" class="row mt-4">
        <div class="col-12">
          <div class="row">
            <div
              v-for="location in sortedLocationResults"
              class="col-sm-6 col-lg-4 col-xl-3 mb-4"
            >
              <ContactSearchLocationCard
                :name="location.name"
                :url="location.url"
                :image="location.image"
                :parent-location="location.parentLocation"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="employeeResults.length" class="row mt-4">
        <div class="col-12">
          <h2 class="text-dark">Contacts</h2>

          <div class="row">
            <div
              v-for="employee in employeeResults"
              class="col-sm-6 col-lg-4 col-xl-3 mb-4"
            >
              <ContactSearchEmployeeCard
                :name="employee.name"
                :position="employee.position"
                :phone="employee.phone"
                :mobile="employee.mobile"
                :email="employee.email"
                :email-split="employee.emailSplit"
                :email-team="employee.emailTeam"
                :email-team-split="employee.emailTeamSplit"
                :vcard="employee.vcard"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ContactSearchForm from "./ContactSearchForm";
import ContactSearchCategoriesFilter from "./ContactSearchCategoriesFilter";
import ContactSearchLocationCard from "./ContactSearchLocationCard";
import ContactSearchEmployeeCard from "./ContactSearchEmployeeCard";

export default {
  name: "ContactSearchFilter",

  components: {
    ContactSearchForm,
    ContactSearchCategoriesFilter,
    ContactSearchLocationCard,
    ContactSearchEmployeeCard,
  },

  props: {
    categories: Array,
    searchEndpoint: String,
  },

  data: () => ({
    dirty: false,
    loading: null,
    selectedCategories: [],
    searchTerm: "",
    locationResults: [],
    employeeResults: [],
  }),

  computed: {
    hasSearchTerms() {
      return this.searchTerm || this.selectedCategories.length;
    },
    hasSearchResults() {
      return this.locationResults.length || this.employeeResults.length;
    },
    sortedLocationResults() {
      return this.locationResults.slice().sort((a, b) => b.order - a.order);
    },
  },

  methods: {
    async performSearch() {
      this.dirty = true;

      if (!this.hasSearchTerms) {
        this.locationResults = [];
        this.employeeResults = [];
        this.loading = null;
        return;
      }

      const loadingStartTime = Date.now();
      this.loading = loadingStartTime;
      const url = new URL(this.searchEndpoint);

      if (this.searchTerm) {
        url.searchParams.append("q", this.searchTerm);
      }

      if (this.selectedCategories.length) {
        url.searchParams.append(
          "categories",
          this.selectedCategories.join(",")
        );
      }

      fetch(url, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (
            this.hasSearchTerms &&
            this.loading &&
            this.loading <= loadingStartTime
          ) {
            this.locationResults = data.locations ?? null;
            this.employeeResults =
              data.employees.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
              }) ?? null;
          }
        })
        .catch((error) => console.error("Something went wrong", error))
        .finally(() => {
          if (this.loading && this.loading <= loadingStartTime) {
            this.loading = null;
          }
        });
    },
  },
};
</script>
